import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button } from 'antd';
import { metersRestCrudThunksForBuyer } from '../../thunks/meters_thunks';
import { EmptyState } from '../empty_state/EmptyState';
import { METERS_CRUD_ACTION_CREATORS } from '../../actions/meters_actions';
import PaginatedReduxTableWithHeader from '../common/PaginatedReduxTableWithHeader';
import { useFilters } from '../../hooks/useFilters';
import { getMeterFilterConfig } from '../../utils/MeterFilterUtils';

require('./MetersIndexPage.less');

const METERS_OVERVIEW_TARGET_COLLECTION = 'metersIndex';
const METER_ADVANCED_FILTERS = 'metersAdvancedFilters';

const MetersIndexPage: FC<any> = ({
	history,
	location,
	meters,
	updateFilters,
	updateSorters,
	clearAndUpdateFilters,
}) => {
	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				let searchString = location.search;
				if (searchString[0] === '?') {
					searchString = searchString.slice(1);
				}
				let backUrl = `/buyer/meters/detail/${record.id}/details`;
				if (searchString && searchString.length > 0) {
					backUrl = backUrl.concat(`?backlinkParams=${encodeURIComponent(searchString)}`);
				}
				history.push(backUrl);
			},
		}),
		[history, location]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Location',
				dataIndex: ['location', 'name'],
				sorter: true,
			},
			{
				title: 'Asset',
				dataIndex: ['asset', 'name'],
				sorter: true,
			},
			{
				title: 'Meter Name',
				dataIndex: 'name',
				sorter: true,
			},
		],
		[]
	);

	const updateFiltersForCollection = useCallback(
		(filters) => updateFilters(filters, METERS_OVERVIEW_TARGET_COLLECTION),
		[updateFilters]
	);

	const allFilters = useFilters({
		filterTargetCollectionName: METER_ADVANCED_FILTERS,
		overviewCollectionName: METERS_OVERVIEW_TARGET_COLLECTION,
		updateFilters: updateFiltersForCollection,
	});

	const filterConfig = useMemo(() => getMeterFilterConfig({ filters: allFilters }), [allFilters]);

	return (
		<PaginatedReduxTableWithHeader
			targetCollectionName={METERS_OVERVIEW_TARGET_COLLECTION}
			stateSlice={meters}
			updateFilters={updateFilters}
			updateSorters={updateSorters}
			clearAndUpdateFilters={clearAndUpdateFilters}
			filterConfig={filterConfig}
			entityCollectionName="meters"
			tableColumns={columns}
			showHeader={true}
			fetchData={metersRestCrudThunksForBuyer.read}
			onTableRow={onRow}
			keyAccessor={(el) => el.id}
			initialPagination={{ current: 1, pageSize: 10 }}
			advancedFiltersInputParamName="name"
			rightActions={
				<Button
					size="large"
					type="primary"
					onClick={() => history.push('/buyer/meters/overview/new')}
				>
					Add Meter
				</Button>
			}
			emptyState={
				<EmptyState
					graphic={
						<img
							style={{ marginBottom: 8 }}
							src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
						/>
					}
					headline={"You haven't created any meters yet."}
					body={
						<div style={{ textAlign: 'center' }}>
							<div style={{ maxWidth: 440, marginBottom: 16 }}></div>
						</div>
					}
				/>
			}
		/>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	meters: state.meters,
});

const mapDispatchToProps = (dispatch) => ({
	updateFilters: (filters, targetCollection) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.updateFilters(filters, targetCollection)),
	updateSorters: (sortBy, order, targetCollection) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.updateSorting(sortBy, order, targetCollection)),
	clearAndUpdateFilters: (filters, targetCollectionName) =>
		dispatch(METERS_CRUD_ACTION_CREATORS.clearAndUpdateFilters(filters, targetCollectionName)),
});

export default withRouter<any, any>(connect(mapStateToProps, mapDispatchToProps)(MetersIndexPage));
